import React, { useEffect, useState } from 'react';
import axios from '../axiosInstance';
import { FaPlus, FaEdit, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { utils, writeFile } from 'xlsx';
import { exportBrandedPdf } from '../utils/brandedPdf';
import '../Pages.css';

const ACTION_OPTIONS = [
  'read',
  'create',
  'update',
  'delete',
  'toggle',
  'import',
  'bulk_search',
  'export',
  'upload',
  'search',
  'assess_risk',
  'upload_document',
  'approve_high_risk',
  'assign_permissions',
];

function ManagePermissions() {
  const [permissions, setPermissions] = useState([]);
  const [modules, setModules] = useState([]);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editPermission, setEditPermission] = useState(null);
  const [permissionName, setPermissionName] = useState('');
  const [permissionDesc, setPermissionDesc] = useState('');
  const [permissionModuleId, setPermissionModuleId] = useState('');
  const [permissionAction, setPermissionAction] = useState('');
  const [msg, setMsg] = useState('');
  const [showMsg, setShowMsg] = useState(false);
  const [expandedModules, setExpandedModules] = useState(() => new Set());

  const toggleModule = (key) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    fetchPermissions();
    fetchModules();
  }, []);

  const fetchPermissions = async () => {
    try {
      const res = await axios.get('/permissions');
      setPermissions(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error fetching permissions:', e);
      setMsg('❌ Failed to fetch permissions');
      setShowMsg(true);
    }
  };

  const fetchModules = async () => {
    try {
      const res = await axios.get('/modules');
      setModules(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error fetching modules:', e);
      setModules([]);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setMsg('');
    setShowMsg(false);

    if (!permissionName.trim()) {
      setMsg('❌ Permission name is required');
      setShowMsg(true);
      return;
    }

    if (!permissionModuleId) {
      setMsg('❌ Module is required');
      setShowMsg(true);
      return;
    }

    const payload = {
      name: permissionName,
      description: permissionDesc,
      module_id: parseInt(permissionModuleId, 10),
    };
    if (permissionAction && permissionAction.trim()) {
      payload.action = permissionAction.trim();
    }

    try {
      if (editPermission) {
        // Update existing permission
        await axios.put(`/permissions/${editPermission.id}`, payload);
        setMsg('✅ Permission updated successfully');
      } else {
        // Create new permission
        await axios.post('/permissions', payload);
        setMsg('✅ Permission created successfully');
      }

      setShowMsg(true);
      resetForm();
      fetchPermissions();
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.error || e.message));
      setShowMsg(true);
    }
  };

  const handleToggleStatus = async (permission) => {
    try {
      await axios.patch(`/permissions/${permission.id}`, { is_active: !permission.is_active });
      setMsg('✅ Permission status updated successfully');
      setShowMsg(true);
      fetchPermissions();
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.error || e.message));
      setShowMsg(true);
    }
  };

  const handleEdit = (permission) => {
    setPermissionName(permission.name);
    setPermissionDesc(permission.description || '');
    setPermissionModuleId(permission.module_id ? String(permission.module_id) : '');
    setPermissionAction(permission.action || '');
    setEditPermission(permission);
    setShowForm(true);
  };

  const resetForm = () => {
    setPermissionName('');
    setPermissionDesc('');
    setPermissionModuleId('');
    setPermissionAction('');
    setEditPermission(null);
    setShowForm(false);
  };

  // Filter by search + module, then sort by module name so rows group naturally.
  const searchLower = search.toLowerCase();
  const filteredList = permissions
    .filter(p => {
      if (moduleFilter && String(p.module_id) !== String(moduleFilter)) return false;
      if (!search) return true;
      return (
        p.name.toLowerCase().includes(searchLower) ||
        (p.description && p.description.toLowerCase().includes(searchLower)) ||
        (p.module_name && p.module_name.toLowerCase().includes(searchLower)) ||
        (p.action && p.action.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => {
      const am = (a.module_name || '').toLowerCase();
      const bm = (b.module_name || '').toLowerCase();
      if (am < bm) return -1;
      if (am > bm) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });

  // Group filtered permissions by module. Build cards in a stable order that
  // follows the `modules` array, then append any leftover groups whose module
  // metadata isn't in `modules` (defensive — falls back to module_name).
  const grouped = new Map();
  filteredList.forEach(p => {
    const key = p.module_id != null ? String(p.module_id) : `__name__:${p.module_name || 'Unassigned'}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        moduleId: p.module_id,
        moduleName: p.module_name || 'Unassigned',
        moduleDescription: '',
        permissions: [],
      });
    }
    grouped.get(key).permissions.push(p);
  });

  // Enrich groups with module description / canonical name from `modules`.
  modules.forEach(m => {
    const key = String(m.id);
    if (grouped.has(key)) {
      grouped.get(key).moduleName = m.name || grouped.get(key).moduleName;
      grouped.get(key).moduleDescription = m.description || '';
    }
  });

  // Order: follow `modules` order first, then any leftover groups alphabetically.
  const orderedGroups = [];
  const seen = new Set();
  modules.forEach(m => {
    const key = String(m.id);
    if (grouped.has(key)) {
      orderedGroups.push(grouped.get(key));
      seen.add(key);
    }
  });
  Array.from(grouped.entries())
    .filter(([k]) => !seen.has(k))
    .sort(([, a], [, b]) => (a.moduleName || '').localeCompare(b.moduleName || ''))
    .forEach(([, group]) => orderedGroups.push(group));

  const handleExportCSV = () => {
    const csvRows = [
      ['#', 'Module', 'Permission', 'Action', 'Description', 'Status'],
      ...filteredList.map((p, i) => [
        i + 1,
        p.module_name || '-',
        p.name,
        p.action || '-',
        p.description || '-',
        p.is_active ? 'Active' : 'Inactive',
      ]),
    ];
    const csv = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'permissions.csv';
    a.click();
  };

  const handleExportExcel = () => {
    const ws = utils.json_to_sheet(filteredList.map((p, i) => ({
      '#': i + 1,
      Module: p.module_name || '-',
      Permission: p.name,
      Action: p.action || '-',
      Description: p.description || '-',
      Status: p.is_active ? 'Active' : 'Inactive',
    })));
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Permissions');
    writeFile(wb, 'permissions.xlsx');
  };

  const handleExportPDF = () => exportBrandedPdf({
    title: 'PERMISSIONS — REPORT',
    searchValue: search ? `"${search}"` : 'All permissions',
    fileName: 'Permissions_Report',
    columns: [
      { header: '#', width: 4, align: 'center' }, { header: 'Module', width: 14 },
      { header: 'Permission', width: 20, bold: true }, { header: 'Action', width: 12 },
      { header: 'Description', width: 38 }, { header: 'Status', width: 8 }
    ],
    rows: filteredList.map((p, i) => [
      i + 1, p.module_name, p.name, p.action, p.description, p.is_active ? 'Active' : 'Inactive'
    ])
  });

  // Inline styles for the cards view. Kept here so we don't touch external CSS.
  const cardStyles = {
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: 16,
      width: '100%',
      margin: '0 auto',
    },
    card: {
      border: '1px solid #e3e6ea',
      borderRadius: 10,
      background: '#fff',
      boxShadow: '0 1px 3px rgba(16,24,40,0.06)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    cardHeader: {
      padding: '14px 16px',
      borderBottom: '1px solid #eef0f3',
      borderLeft: '4px solid #2563eb',
      background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
      cursor: 'pointer',
      userSelect: 'none',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
    },
    cardHeaderText: {
      flex: 1,
      minWidth: 0,
    },
    chevron: {
      flexShrink: 0,
      fontSize: '0.85rem',
      color: '#64748b',
      lineHeight: '1.05rem',
      transform: 'translateY(2px)',
      transition: 'transform 0.15s ease',
    },
    cardTitle: {
      margin: 0,
      fontSize: '1.05rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.3,
      color: '#0f172a',
    },
    cardSubtitle: {
      margin: '4px 0 0',
      fontSize: '0.8rem',
      color: '#64748b',
    },
    cardCount: {
      display: 'inline-block',
      marginTop: 6,
      fontSize: '0.75rem',
      color: '#475569',
      background: '#eef2ff',
      padding: '2px 8px',
      borderRadius: 999,
    },
    cardBody: {
      padding: 8,
      flex: 1,
    },
    permRow: {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: 8,
      alignItems: 'start',
      padding: '10px 8px',
      borderBottom: '1px solid #f1f5f9',
    },
    permRowLast: {
      borderBottom: 'none',
    },
    permAction: {
      fontWeight: 600,
      color: '#0f172a',
      textTransform: 'capitalize',
      fontSize: '0.95rem',
    },
    permSlug: {
      display: 'inline-block',
      marginLeft: 8,
      fontFamily: 'monospace',
      fontSize: '0.75rem',
      background: '#f1f5f9',
      color: '#334155',
      padding: '1px 6px',
      borderRadius: 4,
    },
    permDesc: {
      marginTop: 4,
      fontSize: '0.8rem',
      color: '#64748b',
      lineHeight: 1.35,
    },
    permControls: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      whiteSpace: 'nowrap',
    },
    badgeActive: {
      display: 'inline-block',
      fontSize: '0.7rem',
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: 999,
      background: '#dcfce7',
      color: '#166534',
      marginRight: 6,
    },
    badgeInactive: {
      display: 'inline-block',
      fontSize: '0.7rem',
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: 999,
      background: '#e5e7eb',
      color: '#4b5563',
      marginRight: 6,
    },
    emptyState: {
      padding: 32,
      textAlign: 'center',
      color: '#64748b',
      border: '1px dashed #cbd5e1',
      borderRadius: 10,
      background: '#f8fafc',
    },
  };

  const totalPermissionsShown = filteredList.length;
  const totalModulesShown = orderedGroups.length;

  return (
    <div className="page-container">
      <div className="table-controls">
        <input
          placeholder="Search permissions..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          value={moduleFilter}
          onChange={e => setModuleFilter(e.target.value)}
          style={{ marginLeft: 8 }}
        >
          <option value="">All modules</option>
          {modules.map(m => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <div className="button-group">
          <button className="export-button" onClick={() => { resetForm(); setShowForm(true); }}>
            <FaPlus style={{ marginRight: 5 }} /> Add Permission
          </button>
          <button className="export-button" onClick={handleExportCSV}>Export CSV</button>
          <button className="export-button" onClick={handleExportExcel}>Export Excel</button>
          <button className="export-button" onClick={handleExportPDF}>Export PDF</button>
        </div>
      </div>

      {showForm && (
        <form className="table-container" onSubmit={handleSubmit}>
          <h3>{editPermission ? 'Edit Permission' : 'Add New Permission'}</h3>
          <div className="form-group">
            <label htmlFor="permission-module">Module</label>
            <select
              id="permission-module"
              required
              value={permissionModuleId}
              onChange={e => setPermissionModuleId(e.target.value)}
            >
              <option value="">-- Select Module --</option>
              {modules.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <input
              placeholder="Permission Name (e.g., users.create)"
              required
              value={permissionName}
              onChange={e => setPermissionName(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="permission-action">Action</label>
            <select
              id="permission-action"
              value={permissionAction}
              onChange={e => setPermissionAction(e.target.value)}
            >
              <option value="">-- Select Action (optional) --</option>
              {ACTION_OPTIONS.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <textarea
              placeholder="Description (optional)"
              value={permissionDesc}
              onChange={e => setPermissionDesc(e.target.value)}
              rows="3"
              style={{ resize: 'vertical' }}
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="export-button">
              {editPermission ? 'Update' : 'Submit'}
            </button>
            <button
              type="button"
              className="btn-cancel"
              onClick={resetForm}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {showMsg && (
        <div className="message-popup">
          <div className="message-popup-content">
            <p>{msg}</p>
            <button className="close-popup-button" onClick={() => setShowMsg(false)}>Close</button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '1rem', fontWeight: 'bold', fontSize: '1rem' }}>
        Showing {totalPermissionsShown} permission{totalPermissionsShown === 1 ? '' : 's'} across {totalModulesShown} module{totalModulesShown === 1 ? '' : 's'} {(search || moduleFilter) ? `(filtered from ${permissions.length})` : ''}
      </div>

      {orderedGroups.length === 0 ? (
        <div style={cardStyles.emptyState}>
          {permissions.length === 0
            ? 'No permissions have been created yet.'
            : 'No permissions match the current search or module filter.'}
        </div>
      ) : (
        <div style={cardStyles.grid}>
          {orderedGroups.map(group => {
            const groupKey = `${group.moduleId ?? 'na'}-${group.moduleName}`;
            const isExpanded = expandedModules.has(groupKey);
            return (
            <div key={groupKey} style={cardStyles.card}>
              <div
                style={cardStyles.cardHeader}
                onClick={() => toggleModule(groupKey)}
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleModule(groupKey);
                  }
                }}
              >
                <div style={cardStyles.cardHeaderText}>
                  <h3 style={cardStyles.cardTitle}>{group.moduleName}</h3>
                  {group.moduleDescription ? (
                    <p style={cardStyles.cardSubtitle}>{group.moduleDescription}</p>
                  ) : null}
                  <span style={cardStyles.cardCount}>
                    {group.permissions.length} permission{group.permissions.length === 1 ? '' : 's'}
                  </span>
                </div>
                <span
                  style={{
                    ...cardStyles.chevron,
                    transform: isExpanded ? 'rotate(180deg) translateY(-2px)' : 'translateY(2px)',
                  }}
                  aria-hidden="true"
                >
                  ▾
                </span>
              </div>
              {isExpanded && (
              <div style={cardStyles.cardBody}>
                {group.permissions.map((p, idx) => {
                  const isLast = idx === group.permissions.length - 1;
                  const rowStyle = isLast
                    ? { ...cardStyles.permRow, ...cardStyles.permRowLast }
                    : cardStyles.permRow;
                  const primaryLabel = (p.action && p.action.trim()) || p.name;
                  return (
                    <div
                      key={p.id}
                      style={rowStyle}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div>
                        <div>
                          <span style={cardStyles.permAction}>{primaryLabel}</span>
                          <span style={cardStyles.permSlug}>{p.name}</span>
                        </div>
                        {p.description ? (
                          <div style={cardStyles.permDesc}>{p.description}</div>
                        ) : null}
                      </div>
                      <div style={cardStyles.permControls}>
                        <span
                          style={p.is_active ? cardStyles.badgeActive : cardStyles.badgeInactive}
                          title={p.is_active ? 'Active' : 'Inactive'}
                        >
                          {p.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          className="action-button"
                          onClick={() => handleEdit(p)}
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="action-button"
                          onClick={() => handleToggleStatus(p)}
                          title={p.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {p.is_active ? <FaToggleOn className="toggle-icon--on" /> : <FaToggleOff className="toggle-icon--off" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ManagePermissions;
